import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';
import { get } from 'app/shared/utils/fetchr';
import {
  selectAgentOrgID,
  selectHasReadEntitiesPermissions,
} from 'app/modules/session/selectors';
import { EntityRiskScoreSnapshot } from 'app/modules/riskRatings/models';
import { RiskModelSummary } from 'app/modules/riskRatings/responses';

export interface HistoricRiskScoreRow {
  id: string;
  created_at: string;
  risk_model_id: number;
  risk_model: Pick<
    RiskModelSummary,
    'high_risk_floor' | 'id' | 'low_risk_ceiling' | 'status' | 'title'
  > | null;
  entity_id: string;
  score: number;
  source: 'MANUAL' | 'RISK_MODEL';
  snapshot: EntityRiskScoreSnapshot | null;
}

export type HistoricRiskScoresResponse =
  | { status: 'PENDING' }
  | { status: 'SUCCEEDED'; count: number; results: HistoricRiskScoreRow[] }
  | { status: 'FAILED'; error: string };

export const useGetHistoricRiskScores = (entityExternalId: string) => {
  const hasReadEntitiesPermission = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const orgId = useSelector(selectAgentOrgID);
  const hashedId = useMemo(
    () => computeDlHashIdForCgdo(orgId, entityExternalId),
    [orgId, entityExternalId],
  );

  return useQuery({
    queryFn: () =>
      get<HistoricRiskScoresResponse>(
        `/v1/risk/entities/${hashedId}/historic-scores?limit=200`,
      ),
    queryKey: ENTITY_QUERY_KEYS.getHistoricRiskScores(hashedId),
    refetchInterval: ({ state: { data } }) =>
      data?.status === 'PENDING' ? 2000 : false,
    enabled: hasReadEntitiesPermission,
  });
};
